import React from 'react'
import moment from 'moment'
import { Icon } from 'semantic-ui-react'
import 'moment-duration-format'
import { useTranslation } from 'react-i18next'
import ClockIconFilled from '../../assets/svg/ClockIconFilled.svg'
import AdditionalHourIcon from '../../assets/svg/AdditionalClockedHours.svg'
import { Box, Typography } from '@mui/material'

export interface ITimerProps {
  workedTime: number
  multiplier?: number
  decimals?: boolean
  onTimerUpdate?: (value: number) => void
  color?: () => string | undefined
  isInDialog?: boolean
  showAdditionalClockIcon?: boolean
  isInActions?: boolean
}

const Timer: React.FC<ITimerProps> = ({
  workedTime,
  multiplier = 1,
  decimals = false,
  onTimerUpdate,
  color,
  isInDialog = false,
  showAdditionalClockIcon = false,
  isInActions = false
}) => {
  const [seconds, setSeconds] = React.useState(workedTime)
  const duration = moment.duration(seconds, 's')
  const { t } = useTranslation()
  const bgColor = color?.()

  const addSecond = () => {
    setSeconds(seconds + multiplier)
  }

  React.useEffect(() => {
    if (workedTime !== seconds) {
      setSeconds(workedTime)
    }
  }, [workedTime])

  React.useEffect(() => {
    const timeout = window.setTimeout(addSecond, 1000)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [seconds])

  React.useEffect(() => {
    if (onTimerUpdate) {
      onTimerUpdate(seconds) // Pass the current seconds value to the parent
    }
  }, [seconds, onTimerUpdate])

  return (
    <Box display='flex' flexDirection='column' justifyContent='end' textAlign='right'>
      {isInDialog ? (
        <Box display='flex' alignItems='center' data-testid='ticking-timer'>
          {showAdditionalClockIcon ? (
            <AdditionalHourIcon width={18} height={18} viewBox='0 -1 28 28' data-testid='additional-hour-icon' />
          ) : (
            <ClockIconFilled width={18} height={18} viewBox='0 -2 28 28' data-testid='clock-icon-amber' />
          )}
          &nbsp;<Typography style={{ color: bgColor, fontWeight: 'bold' }}>{duration.format('HH:mm:ss', { trim: false })}</Typography>
        </Box>
      ) : (
        <Box display='flex' justifyContent={isInActions ? 'center' : 'end'}>
          <Icon name='clock outline' style={{ marginTop: isInActions ? '0px' : '1px' }} />
          {isInActions ? (
            <span style={{ color: bgColor }}>{duration.format('HH:mm:ss', { trim: false })}</span>
          ) : (
            <Typography style={{ color: bgColor }}>{duration.format('HH:mm:ss', { trim: false })}</Typography>
          )}
        </Box>
      )}
      {decimals ? (
        <Typography style={{ display: 'block', textAlign: 'right', paddingTop: '2px', fontWeight: isInDialog ? 'bold' : 'normal' }}>
          {duration.format(`h [${t('Timer.hrs')}]`, 2)}
        </Typography>
      ) : null}
    </Box>
  )
}

Timer.displayName = 'Timer'

export default Timer
